import { zodResolver } from '@hookform/resolvers/zod'
import { router } from 'expo-router'
import { useForm } from 'react-hook-form'
import { KeyboardAvoidingView } from 'react-native'
import { Portal } from 'react-native-paper'
import { StyleSheet } from 'react-native-unistyles'
import z from 'zod'

import FormFieldText from '@/components/form/FormFieldText'
import UniButton from '@/components/unistyles/UniButton'
import UniModal from '@/components/unistyles/UniModal'
import UniText from '@/components/unistyles/UniText'
import { useAppStore } from '@/store/useAppStore'
import buildOrgPath from '@/utils/orgRoute'
import trpc from '@/utils/trpc'

interface Props {
  open: boolean
  onClose: () => void
}

const newFosterFormSchema = z.object({
  name: z.string().min(3, 'Names must be at least 3 characters'),
})

type NewFosterForm = z.infer<typeof newFosterFormSchema>

const NewFoster = ({ open, onClose }: Props) => {
  const currentOrganizationId =
    useAppStore.use.currentOrganization().organization?.id

  const newFosterMutation = trpc.foster.create.useMutation()

  const { control, formState, handleSubmit, reset } = useForm<NewFosterForm>({
    mode: 'onBlur',
    resolver: zodResolver(newFosterFormSchema),
    defaultValues: {
      name: '',
    },
  })

  if (!currentOrganizationId) {
    return null
  }

  const onSubmit = handleSubmit(async (data) => {
    const response = await newFosterMutation.mutateAsync({
      ...data,
      organizationId: currentOrganizationId,
    })

    router.push({
      pathname: buildOrgPath('/fosters/[id]'),
      params: { id: response.id, from: 'fosters' },
    })

    reset()
    onClose()
  })

  return (
    <Portal>
      <KeyboardAvoidingView
        behavior="height"
        enabled={open ? true : false}
        style={{
          height: open ? '100%' : 'auto',
        }}
      >
        <UniModal
          contentContainerStyle={styles.root}
          onDismiss={onClose}
          visible={open}
        >
          <UniText variant="titleSmall">Enter a name for your foster.</UniText>
          <FormFieldText
            autoFocus
            control={control}
            errors={formState.errors}
            fieldName="name"
            helperText="You can change this later."
            label="Name"
            required
            submitBehavior="blurAndSubmit"
          />
          <UniButton
            disabled={newFosterMutation.isPending}
            loading={newFosterMutation.isPending}
            mode="contained"
            onPress={onSubmit}
          >
            Add Foster
          </UniButton>
        </UniModal>
      </KeyboardAvoidingView>
    </Portal>
  )
}

const styles = StyleSheet.create((theme) => {
  return {
    root: {
      backgroundColor: theme.colors.surface,
      borderRadius: theme.tokens.containerBorderRadius,
      gap: theme.tokens.spacing[4],
      margin: 'auto',
      padding: theme.tokens.spacing[6],
      width: 300,
    },
  }
})

export default NewFoster
